import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import { ArrowLeft, Save, Send, TestTube2 } from 'lucide-react';
import type { RouteOffer, OfferRecipient } from '@/types/routeOffers';
import type { QuoteRequest } from '@/types';
import { collection, getDocs, addDoc, updateDoc, doc, getDoc, serverTimestamp, query, where, limit, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { getQuoteById, updateQuoteStatus } from '@/services/quoteService';

/**
 * Form-specific recipient type (internal structure)
 * Gets converted to OfferRecipient when saving to Firestore
 */
interface FormRecipient {
  userId: string;
  userEmail: string;
  userName: string;
  studentCount: number;
  tier: 'tier1' | 'tier2';
  status: 'pending';
  // Optional fields from loaded offers
  studentIds?: string[];
  weeklyRate?: number;
  selectedWeeks?: number[];
  subtotal?: number;
  totalAmount?: number;
  quoteId?: string;
  enrollmentId?: string;
}

/**
 * Look up a user's Firebase UID by their email address
 * Returns the UID if found, otherwise returns null
 */
async function lookupUserIdByEmail(email: string): Promise<string | null> {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', email),
      limit(1)
    );
    const usersSnapshot = await getDocs(usersQuery);

    if (!usersSnapshot.empty) {
      const userDoc = usersSnapshot.docs[0];
      return userDoc.id; // Document ID is the Firebase UID
    }

    console.warn(`[lookupUserIdByEmail] User not found for email: ${email}`);
    return null; // Return null if user doesn't exist
  } catch (error) {
    console.error(`[lookupUserIdByEmail] Error looking up user:`, error);
    return null; // Return null on error
  }
}

interface OfferFormData {
  // Route Info
  routeName: string;
  routeCategory: 'Summer Camp';
  pickupLocation: string;
  pickupTime: string;
  pickupInstructions: string;
  dropoffLocation: string;
  dropoffTime: string;
  dropoffInstructions: string;
  maxStudents: number;

  // Notes
  adminNotes: string;
  customerMessage: string;

  // Availability
  availableWeeks: number[];
  allowPartialWeeks: boolean;

  // Recipients
  recipients: FormRecipient[];

  // Pricing
  baseWeeklyRate: number;
  percentageAdjustment: number;

  // Settings
  expiresInDays: number;
  autoOfferWaitlist: boolean;
  sendImmediately: boolean;
}

export default function CreateRouteOffer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: routeParamId } = useParams<{ id: string }>();
  const { user, role } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [loadingOffer, setLoadingOffer] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [sourceQuote, setSourceQuote] = useState<QuoteRequest | null>(null);
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
  const [formData, setFormData] = useState<OfferFormData>({
    routeName: '',
    routeCategory: 'Summer Camp',
    pickupLocation: '',
    pickupTime: '7:30 AM',
    pickupInstructions: '',
    dropoffLocation: '',
    dropoffTime: '5:00 PM',
    dropoffInstructions: '',
    maxStudents: 12,
    adminNotes: '',
    customerMessage: '',
    availableWeeks: [],
    allowPartialWeeks: true,
    recipients: [],
    baseWeeklyRate: 165,
    percentageAdjustment: 0,
    expiresInDays: 14,
    autoOfferWaitlist: true,
    sendImmediately: true,
  });

  // Redirect if not admin
  React.useEffect(() => {
    if (role && role !== 'admin') {
      navigate('/dashboard');
      toast({
        title: 'Access Denied',
        description: 'You need admin privileges to access this page.',
        variant: 'destructive',
      });
    }
  }, [role, navigate, toast]);

  // Load quote if quoteId parameter is present
  useEffect(() => {
    const quoteId = searchParams.get('quoteId');
    if (!quoteId || !user || role !== 'admin') {
      return;
    }

    const loadQuote = async () => {
      try {
        setLoadingQuote(true);
        const quote = await getQuoteById(quoteId);

        if (!quote) {
          toast({
            title: 'Quote Not Found',
            description: 'The quote request could not be found.',
            variant: 'destructive',
          });
          return;
        }

        setSourceQuote(quote);

        // Look up user ID by email
        const userId = await lookupUserIdByEmail(quote.guardianEmail);
        if (!userId) {
          toast({
            title: 'User Account Required',
            description: `No account found for ${quote.guardianEmail}. The guardian must create an account before receiving an offer.`,
            variant: 'destructive',
          });
          return;
        }

        // Pre-populate form with quote data
        const suggestedRouteName = quote.routeName ||
          `${quote.pickupZipcode || 'Pickup'} to ${quote.dropoffAddress.split(',')[0] || 'Destination'} - Summer 2026`;

        setFormData(prev => ({
          ...prev,
          routeName: suggestedRouteName,
          pickupLocation: quote.pickupAddress,
          dropoffLocation: quote.dropoffAddress,
          recipients: [{
            userId,
            userEmail: quote.guardianEmail,
            userName: quote.guardianName,
            studentCount: quote.students.length,
            tier: 'tier1' as any,
            status: 'pending' as any,
          }],
          adminNotes: `Created from quote request #${quoteId}\nOriginal request date: ${quote.createdAt.toLocaleDateString()}`,
          availableWeeks: quote.selectedSessions?.map(s => parseInt(s.replace('week', ''))) || [],
        }));

        toast({
          title: 'Quote Loaded',
          description: `Pre-filled with data from ${quote.guardianName}'s quote request.`,
        });
      } catch (error) {
        console.error('[CreateRouteOffer] Error loading quote:', error);
        toast({
          title: 'Error',
          description: 'Failed to load quote data. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoadingQuote(false);
      }
    };

    loadQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user, role]);

  // Load existing offer if offerId parameter is present (edit mode)
  useEffect(() => {
    console.log('[CreateRouteOffer] Load offer useEffect triggered. routeParamId:', routeParamId, 'searchParams offerId:', searchParams.get('offerId'), 'user:', !!user, 'role:', role);

    // Check both route params and query params for offerId
    const offerId = routeParamId || searchParams.get('offerId');
    if (!offerId || !user || role !== 'admin') {
      console.log('[CreateRouteOffer] Skipping load - missing requirements');
      return;
    }

    const loadOffer = async () => {
      try {
        console.log('[CreateRouteOffer] Starting to load offer:', offerId);
        setLoadingOffer(true);
        const offerRef = doc(db, 'routeOffers', offerId);
        const offerDoc = await getDoc(offerRef);

        console.log('[CreateRouteOffer] Offer doc exists:', offerDoc.exists());

        if (!offerDoc.exists()) {
          console.error('[CreateRouteOffer] Offer not found:', offerId);
          toast({
            title: 'Offer Not Found',
            description: 'The route offer could not be found.',
            variant: 'destructive',
          });
          return;
        }

        const offerData = offerDoc.data();
        console.log('[CreateRouteOffer] Offer data loaded:', offerData.routeName);
        setEditingOfferId(offerId);

        // Calculate expiration days from expiresAt
        const expiresAt = offerData.expiresAt?.toDate ? offerData.expiresAt.toDate() : new Date(offerData.expiresAt);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        // Pre-populate form with existing offer data
        // Map recipients from Firestore format to form format
        const mappedRecipients = (offerData.recipients || []).map((r: any) => ({
          userId: r.userId,
          userEmail: r.email,
          userName: r.name,
          studentCount: r.studentCount,
          tier: r.tier === 1 ? 'tier1' : 'tier2',
          status: r.status,
          // Preserve other fields
          studentIds: r.studentIds,
          weeklyRate: r.weeklyRate,
          selectedWeeks: r.selectedWeeks,
          subtotal: r.subtotal,
          totalAmount: r.totalAmount,
          quoteId: r.quoteId,
          enrollmentId: r.enrollmentId,
        }));

        setFormData(prev => ({
          ...prev,
          routeName: offerData.routeName || '',
          pickupLocation: offerData.pickupLocation || '',
          pickupTime: offerData.pickupTime || '7:30 AM',
          pickupInstructions: offerData.pickupInstructions || '',
          dropoffLocation: offerData.dropoffLocation || '',
          dropoffTime: offerData.dropoffTime || '5:00 PM',
          dropoffInstructions: offerData.dropoffInstructions || '',
          maxStudents: offerData.maxStudents || 12,
          adminNotes: offerData.adminNotes || '',
          customerMessage: offerData.customerMessage || '',
          availableWeeks: offerData.availableWeeks || [],
          allowPartialWeeks: offerData.allowPartialWeeks ?? true,
          recipients: mappedRecipients,
          baseWeeklyRate: offerData.baseWeeklyRate || 165,
          percentageAdjustment: offerData.percentageAdjustment || 0,
          expiresInDays: daysUntilExpiry > 0 ? daysUntilExpiry : 14,
          autoOfferWaitlist: offerData.autoOfferWaitlist ?? true,
          sendImmediately: true,
        }));

        console.log('[CreateRouteOffer] Form data updated with offer. Recipients count:', mappedRecipients.length);

        toast({
          title: 'Editing Offer',
          description: `Loaded existing offer: ${offerData.routeName}`,
        });
      } catch (error) {
        console.error('[CreateRouteOffer] Error loading offer:', error);
        toast({
          title: 'Error',
          description: 'Failed to load offer data. Please try again.',
          variant: 'destructive',
        });
      } finally {
        console.log('[CreateRouteOffer] Setting loadingOffer to false');
        setLoadingOffer(false);
      }
    };

    loadOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParamId, searchParams, user, role]);

  const updateFormData = (updates: Partial<OfferFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleSaveDraft = async () => {
    try {
      setLoading(true);

      const offerId = await createRouteOffer({
        ...formData,
        sendImmediately: false,
      });

      toast({
        title: 'Draft Saved',
        description: 'Your route offer has been saved as a draft.',
      });

      navigate(`/admin/route-offers/${offerId}`);
    } catch (error: any) {
      console.error('Error saving draft:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save draft. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setSendingTestEmail(true);

      // Validation
      if (!formData.routeName.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Please enter a route name before sending test email.',
          variant: 'destructive',
        });
        setSendingTestEmail(false);
        return;
      }

      if (!formData.pickupLocation.trim() || !formData.dropoffLocation.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Please enter pickup and dropoff locations.',
          variant: 'destructive',
        });
        setSendingTestEmail(false);
        return;
      }

      const adjustedWeeklyRate = Math.round(
        formData.baseWeeklyRate * (1 + formData.percentageAdjustment / 100) * 100
      ) / 100;

      const sendTestEmail = httpsCallable(functions, 'sendTestRouteOfferEmail');
      const result = await sendTestEmail({
        routeName: formData.routeName,
        pickupLocation: formData.pickupLocation,
        dropoffLocation: formData.dropoffLocation,
        pickupTime: formData.pickupTime || '',
        dropoffTime: formData.dropoffTime || '',
        availableWeeks: formData.availableWeeks,
        weeklyRate: adjustedWeeklyRate,
        studentCount: formData.recipients.reduce((sum, r) => sum + r.studentCount, 0) || 1,
        customerMessage: formData.customerMessage || '',
      });

      const data = result.data as any;

      toast({
        title: 'Test Email Sent!',
        description: `Preview email sent to ${data.sentTo}`,
      });
    } catch (error: any) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send test email. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSendOffer = async () => {
    try {
      setLoading(true);

      // Validation
      if (!formData.routeName.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Please enter a route name.',
          variant: 'destructive',
        });
        setCurrentStep(1);
        setLoading(false);
        return;
      }

      if (!formData.pickupLocation.trim() || !formData.dropoffLocation.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Please enter pickup and dropoff locations.',
          variant: 'destructive',
        });
        setCurrentStep(1);
        setLoading(false);
        return;
      }

      if (formData.availableWeeks.length === 0) {
        toast({
          title: 'Validation Error',
          description: 'Please select at least one available week.',
          variant: 'destructive',
        });
        setCurrentStep(3);
        setLoading(false);
        return;
      }

      if (formData.recipients.length === 0) {
        toast({
          title: 'Validation Error',
          description: 'Please add at least one recipient.',
          variant: 'destructive',
        });
        setCurrentStep(4);
        setLoading(false);
        return;
      }

      // Write directly to Firestore (bypass Cloud Function CORS issue)
      const adjustedWeeklyRate = Math.round(
        formData.baseWeeklyRate * (1 + formData.percentageAdjustment / 100) * 100
      ) / 100;

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + formData.expiresInDays);

      const offerData: any = {
        routeName: formData.routeName,
        routeCategory: formData.routeCategory,
        pickupLocation: formData.pickupLocation,
        pickupTime: formData.pickupTime || '',
        pickupInstructions: formData.pickupInstructions || '',
        dropoffLocation: formData.dropoffLocation,
        dropoffTime: formData.dropoffTime || '',
        dropoffInstructions: formData.dropoffInstructions || '',
        maxStudents: formData.maxStudents,
        availableWeeks: formData.availableWeeks,
        allowPartialWeeks: formData.allowPartialWeeks,
        baseWeeklyRate: formData.baseWeeklyRate,
        percentageAdjustment: formData.percentageAdjustment,
        adjustedWeeklyRate,
        adminNotes: formData.adminNotes || '',
        customerMessage: formData.customerMessage || '',
        expiresInDays: formData.expiresInDays,
        expiresAt: expiresAt, // Store as Date object, Firestore will convert to Timestamp
        status: formData.sendImmediately ? 'active' : 'draft',
        recipients: formData.recipients.map((r) => ({
          userId: r.userId,
          email: r.userEmail,
          name: r.userName,
          studentCount: r.studentCount,
          studentIds: [],
          tier: r.tier === 'tier1' ? 1 : 2,
          weeklyRate: adjustedWeeklyRate, // Add weeklyRate for pricing calculation
          subtotal: 0, // Will be calculated when customer selects weeks
          totalAmount: 0, // Will be calculated when customer selects weeks
          selectedWeeks: [],
          status: 'pending',
          ...(sourceQuote?.id && { quoteId: sourceQuote.id }), // Link to original quote if this offer was created from a quote
        })),
        totalRecipientsCount: formData.recipients.length,
        activeRecipientsCount: formData.recipients.length,
        recipientUserIds: formData.recipients.map((r) => r.userId), // Helper field for easier querying
        updatedAt: serverTimestamp(),
        sentAt: formData.sendImmediately ? serverTimestamp() : null,
      };

      // Only set currentEnrollment when creating new offer (not when editing to preserve existing enrollment)
      if (!editingOfferId) {
        offerData.currentEnrollment = 0;
        offerData.createdAt = serverTimestamp();
        offerData.createdBy = user?.uid || '';
      }

      let offerId: string;

      if (editingOfferId) {
        // Update existing offer
        const offerRef = doc(db, 'routeOffers', editingOfferId);

        // Filter out undefined values to avoid Firestore errors
        const updateData: any = {};
        Object.keys(offerData).forEach(key => {
          if (offerData[key] !== undefined) {
            updateData[key] = offerData[key];
          }
        });
        updateData.updatedAt = serverTimestamp();

        await updateDoc(offerRef, updateData);
        offerId = editingOfferId;

        toast({
          title: 'Offer Updated',
          description: 'Route offer has been updated successfully.',
        });
      } else {
        // Create new offer
        const docRef = await addDoc(collection(db, 'routeOffers'), offerData);
        offerId = docRef.id;

        // If created from a quote, update the quote with offerId and status
        if (sourceQuote) {
          try {
            const quoteRef = doc(db, 'quoteRequests', sourceQuote.id);
            await updateDoc(quoteRef, {
              offerId: offerId,
              status: 'quoted',
              quotedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
          } catch (error) {
            console.error('[CreateRouteOffer] Error updating quote:', error);
            // Don't fail the whole operation if quote update fails
          }
        }

        toast({
          title: formData.sendImmediately ? 'Offer Sent' : 'Offer Created',
          description: formData.sendImmediately
            ? 'Route offer has been sent to recipients.'
            : 'Route offer has been created and can be sent later.',
        });
      }

      navigate('/admin/route-offers');
    } catch (error: any) {
      console.error('Error sending offer:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create offer. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Route Information', description: 'Basic route details and capacity' },
    { number: 2, title: 'Notes & Instructions', description: 'Internal notes and customer message' },
    { number: 3, title: 'Week Availability', description: 'Select available weeks' },
    { number: 4, title: 'Recipients', description: 'Add customers and waitlist' },
    { number: 5, title: 'Pricing', description: 'Set rates and adjustments' },
    { number: 6, title: 'Review & Send', description: 'Review and send offer' },
  ];

  const adjustedRate = formData.baseWeeklyRate * (1 + formData.percentageAdjustment / 100);

  if (loadingQuote || loadingOffer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">{loadingQuote ? 'Loading quote data...' : 'Loading offer data...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/route-offers')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Route Offers
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">{editingOfferId ? 'Edit Route Offer' : 'Create Route Offer'}</h1>
          <p className="text-gray-600 mt-1">
            {editingOfferId ? 'Update transportation offer details' : 'Set up a new transportation offer for customers'}
          </p>
        </div>

        {/* Quote Source Banner */}
        {sourceQuote && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900">Creating Offer from Quote Request</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Pre-filled with data from <span className="font-medium">{sourceQuote.guardianName}</span>'s quote request
                  submitted on {sourceQuote.createdAt.toLocaleDateString()}.
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  You can modify any details before sending the offer. The quote will be automatically marked as "quoted" when you send this offer.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => setCurrentStep(step.number)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                      currentStep === step.number
                        ? 'bg-primary text-white'
                        : currentStep > step.number
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {step.number}
                  </button>
                  <div className="text-xs text-center mt-2 max-w-[100px]">
                    <div className="font-medium">{step.title}</div>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 ${currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{steps[currentStep - 1].title}</CardTitle>
            <CardDescription>{steps[currentStep - 1].description}</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {/* Step 1: Route Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Route Name *
                  </label>
                  <input
                    type="text"
                    value={formData.routeName}
                    onChange={(e) => updateFormData({ routeName: e.target.value })}
                    placeholder="e.g., Chestnut Hill to Science Center High - Summer 2026"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <input
                      type="text"
                      value={formData.routeCategory}
                      disabled
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Maximum Students *
                    </label>
                    <input
                      type="number"
                      value={formData.maxStudents}
                      onChange={(e) => updateFormData({ maxStudents: parseInt(e.target.value) })}
                      min="1"
                      max="50"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Pickup */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Pickup Details</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pickup Location *
                      </label>
                      <input
                        type="text"
                        value={formData.pickupLocation}
                        onChange={(e) => updateFormData({ pickupLocation: e.target.value })}
                        placeholder="Address or area name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pickup Time
                      </label>
                      <input
                        type="text"
                        value={formData.pickupTime}
                        onChange={(e) => updateFormData({ pickupTime: e.target.value })}
                        placeholder="e.g., 7:30 AM"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pickup Instructions (Optional)
                      </label>
                      <textarea
                        value={formData.pickupInstructions}
                        onChange={(e) => updateFormData({ pickupInstructions: e.target.value })}
                        placeholder="Special instructions for pickup"
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Dropoff */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Dropoff Details</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dropoff Location *
                      </label>
                      <input
                        type="text"
                        value={formData.dropoffLocation}
                        onChange={(e) => updateFormData({ dropoffLocation: e.target.value })}
                        placeholder="Science Center High School, 3101 W Allegheny Ave"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dropoff Time
                      </label>
                      <input
                        type="text"
                        value={formData.dropoffTime}
                        onChange={(e) => updateFormData({ dropoffTime: e.target.value })}
                        placeholder="e.g., 5:00 PM"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dropoff Instructions (Optional)
                      </label>
                      <textarea
                        value={formData.dropoffInstructions}
                        onChange={(e) => updateFormData({ dropoffInstructions: e.target.value })}
                        placeholder="Special instructions for dropoff"
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Notes */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Internal Admin Notes
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    These notes are only visible to admins and will not be shown to customers.
                  </p>
                  <textarea
                    value={formData.adminNotes}
                    onChange={(e) => updateFormData({ adminNotes: e.target.value })}
                    placeholder="Internal notes about this offer..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer-Facing Message (Optional)
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    This message will be included in the offer email sent to customers.
                  </p>
                  <textarea
                    value={formData.customerMessage}
                    onChange={(e) => updateFormData({ customerMessage: e.target.value })}
                    placeholder="We're excited to offer you transportation for summer camp 2026..."
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Week Availability */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-4 mb-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.allowPartialWeeks}
                      onChange={(e) => updateFormData({ allowPartialWeeks: e.target.checked })}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Allow customers to select individual weeks
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Available Weeks (Summer Camp 2026)
                  </label>
                  <div className="grid grid-cols-5 gap-3">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((week) => (
                      <button
                        key={week}
                        onClick={() => {
                          const isSelected = formData.availableWeeks.includes(week);
                          updateFormData({
                            availableWeeks: isSelected
                              ? formData.availableWeeks.filter((w) => w !== week)
                              : [...formData.availableWeeks, week].sort((a, b) => a - b),
                          });
                        }}
                        className={`p-4 rounded-lg border-2 font-semibold transition-colors ${
                          formData.availableWeeks.includes(week)
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                        }`}
                      >
                        Week {week}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-4">
                    {formData.availableWeeks.length} of 10 weeks selected
                  </p>
                </div>
              </div>
            )}

            {/* Step 4: Recipients */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Recipients</h3>
                    <p className="text-sm text-gray-600">
                      Add customers to receive this offer
                    </p>
                  </div>
                </div>

                {/* Browse existing quotes */}
                <div className="space-y-4">
                  <div className="border-b pb-4 mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">Import from Existing Quotes</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Search for submitted quotes by destination to add recipients
                    </p>
                    <div className="flex gap-2 mb-3">
                      <input
                        type="text"
                        id="quote-search-input"
                        placeholder="Search by destination address or zipcode..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        onChange={async (e) => {
                          const searchTerm = e.target.value.trim().toLowerCase();
                          if (searchTerm.length >= 3) {
                            // Search quotes in real-time
                            const quotesRef = collection(db, 'quoteRequests');
                            const quotesSnapshot = await getDocs(quotesRef);
                            const searchResults = quotesSnapshot.docs
                              .filter((doc) => {
                                const data = doc.data();
                                const destination = (data.dropoffAddress || '').toLowerCase();
                                const pickupZip = (data.pickupZipcode || '').toLowerCase();
                                return destination.includes(searchTerm) || pickupZip.includes(searchTerm);
                              })
                              .slice(0, 10); // Limit to 10 results

                            // Store results in a state (we'll add this)
                            const resultsContainer = document.getElementById('quote-search-results');
                            if (resultsContainer) {
                              resultsContainer.innerHTML = searchResults.map((doc) => {
                                const data = doc.data();
                                return `
                                  <div class="p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-quote-id="${doc.id}">
                                    <div class="font-medium">${data.guardianInfo?.firstName || ''} ${data.guardianInfo?.lastName || ''}</div>
                                    <div class="text-sm text-gray-600">${data.guardianInfo?.email || ''}</div>
                                    <div class="text-sm text-gray-500 mt-1">📍 ${data.pickupAddress || 'N/A'} → ${data.dropoffAddress || 'N/A'}</div>
                                    <div class="text-sm text-gray-500">${data.students?.length || 0} student(s)</div>
                                  </div>
                                `;
                              }).join('') || '<div class="p-3 text-sm text-gray-500">No quotes found</div>';
                            }
                          }
                        }}
                      />
                    </div>
                    <div id="quote-search-results" className="space-y-2 max-h-60 overflow-y-auto" onClick={async (e) => {
                      const target = e.target as HTMLElement;
                      const quoteCard = target.closest('[data-quote-id]') as HTMLElement;
                      if (quoteCard) {
                        const quoteId = quoteCard.getAttribute('data-quote-id');
                        // Import this quote's recipient
                        getDocs(collection(db, 'quoteRequests')).then(async (snapshot) => {
                          const quoteDoc = snapshot.docs.find(doc => doc.id === quoteId);
                          if (quoteDoc) {
                            const data = quoteDoc.data();
                            const email = data.guardianInfo?.email;
                            const studentCount = data.students?.length || 1;
                            if (email) {
                              const userId = await lookupUserIdByEmail(email);
                              if (!userId) {
                                toast({
                                  title: 'User Not Found',
                                  description: `No account found for ${email}. The user must sign up before you can create an offer for them.`,
                                  variant: 'destructive',
                                });
                                return;
                              }
                              updateFormData({
                                recipients: [
                                  ...formData.recipients,
                                  {
                                    userId,
                                    userEmail: email,
                                    userName: `${data.guardianInfo?.firstName || ''} ${data.guardianInfo?.lastName || ''}`.trim() || email.split('@')[0],
                                    studentCount,
                                    tier: 'tier1',
                                    status: 'pending',
                                  },
                                ],
                              });
                              toast({
                                title: 'Recipient Added',
                                description: `Added ${email} from quote (${studentCount} student${studentCount > 1 ? 's' : ''})`,
                              });
                              // Clear search
                              const searchInput = document.getElementById('quote-search-input') as HTMLInputElement;
                              if (searchInput) searchInput.value = '';
                              const resultsContainer = document.getElementById('quote-search-results');
                              if (resultsContainer) resultsContainer.innerHTML = '';
                            }
                          }
                        });
                      }
                    }}></div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Or Add Manually</h4>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add Recipient by Email
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        id="recipient-email-input"
                        placeholder="user@example.com"
                        className="flex-[2] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const emailInput = e.target as HTMLInputElement;
                            const studentCountInput = document.getElementById('recipient-student-count') as HTMLInputElement;
                            const email = emailInput.value.trim();
                            const studentCount = parseInt(studentCountInput.value) || 1;
                            if (email && email.includes('@')) {
                              // Add recipient with email (userId will be looked up by Cloud Function)
                              updateFormData({
                                recipients: [
                                  ...formData.recipients,
                                  {
                                    userId: email, // Will be converted to UID by lookupUserIdByEmail // Temporarily use email as userId
                                    userEmail: email,
                                    userName: email.split('@')[0], // Use email prefix as temp name
                                    studentCount,
                                    tier: formData.recipients.length === 0 ? 'tier1' : 'tier1',
                                    status: 'pending',
                                  },
                                ],
                              });
                              emailInput.value = '';
                              studentCountInput.value = '1';
                              toast({
                                title: 'Recipient Added',
                                description: `Added ${email} with ${studentCount} student${studentCount > 1 ? 's' : ''}`,
                              });
                            } else {
                              toast({
                                title: 'Invalid Email',
                                description: 'Please enter a valid email address',
                                variant: 'destructive',
                              });
                            }
                          }
                        }}
                      />
                      <input
                        type="number"
                        id="recipient-student-count"
                        placeholder="# students"
                        min="1"
                        defaultValue="1"
                        className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                      <Button
                        onClick={async () => {
                          const emailInput = document.getElementById('recipient-email-input') as HTMLInputElement;
                          const studentCountInput = document.getElementById('recipient-student-count') as HTMLInputElement;
                          const email = emailInput.value.trim();
                          const studentCount = parseInt(studentCountInput.value) || 1;
                          if (email && email.includes('@')) {
                            const userId = await lookupUserIdByEmail(email);
                            if (!userId) {
                              toast({
                                title: 'User Not Found',
                                description: `No account found for ${email}. The user must sign up before you can create an offer for them.`,
                                variant: 'destructive',
                              });
                              return;
                            }
                            updateFormData({
                              recipients: [
                                ...formData.recipients,
                                {
                                  userId,
                                  userEmail: email,
                                  userName: email.split('@')[0],
                                  studentCount,
                                  tier: formData.recipients.length === 0 ? 'tier1' : 'tier1',
                                  status: 'pending',
                                },
                              ],
                            });
                            emailInput.value = '';
                            studentCountInput.value = '1';
                            toast({
                              title: 'Recipient Added',
                              description: `Added ${email} with ${studentCount} student${studentCount > 1 ? 's' : ''}`,
                            });
                          } else {
                            toast({
                              title: 'Invalid Email',
                              description: 'Please enter a valid email address',
                              variant: 'destructive',
                            });
                          }
                        }}
                      >
                        Add Recipient
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Enter email and number of students, then press Enter or click "Add Recipient"
                    </p>
                  </div>
                </div>

                {formData.recipients.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-gray-600 mb-4">No recipients added yet</p>
                    <p className="text-sm text-gray-500">
                      Enter an email address above to add a recipient
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.recipients.map((recipient, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg"
                      >
                        <div>
                          <div className="font-medium">{recipient.userName}</div>
                          <div className="text-sm text-gray-600">{recipient.userEmail}</div>
                          <div className="text-sm text-gray-500">
                            {recipient.studentCount} student{recipient.studentCount > 1 ? 's' : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            recipient.tier === 'tier1' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {recipient.tier === 'tier1' ? 'Priority' : 'Waitlist'}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              updateFormData({
                                recipients: formData.recipients.filter((_, i) => i !== index),
                              });
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Pricing */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Base Weekly Rate (from quote engine)
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">$</span>
                      <input
                        type="number"
                        value={formData.baseWeeklyRate}
                        onChange={(e) => updateFormData({ baseWeeklyRate: parseFloat(e.target.value) })}
                        step="0.01"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                      <span className="text-gray-500">/ week</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Percentage Adjustment
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={formData.percentageAdjustment}
                        onChange={(e) => updateFormData({ percentageAdjustment: parseFloat(e.target.value) })}
                        step="1"
                        min="-100"
                        max="100"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                      <span className="text-gray-500">%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Use negative for discount (e.g., -5%), positive for increase (e.g., +10%)
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Adjusted Rate</h4>
                  <div className="text-3xl font-bold text-blue-900">
                    ${adjustedRate.toFixed(2)} <span className="text-lg text-blue-700">/ week per student</span>
                  </div>
                  {formData.percentageAdjustment !== 0 && (
                    <p className="text-sm text-blue-700 mt-2">
                      {formData.percentageAdjustment > 0 ? '+' : ''}
                      {formData.percentageAdjustment}% adjustment from base rate of ${formData.baseWeeklyRate.toFixed(2)}
                    </p>
                  )}
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-1">Projected Route Revenue</h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Based on {formData.recipients.length} recipient{formData.recipients.length !== 1 ? 's' : ''} × {formData.availableWeeks.length} week{formData.availableWeeks.length !== 1 ? 's' : ''}
                  </p>
                  {formData.recipients.length > 0 && formData.availableWeeks.length > 0 ? (
                    <div className="space-y-2 text-sm">
                      {formData.recipients.map((recipient, idx) => {
                        const revenue = recipient.studentCount * formData.availableWeeks.length * adjustedRate;
                        return (
                          <div key={idx} className="flex justify-between text-gray-600">
                            <span>{recipient.userName} ({recipient.studentCount} student{recipient.studentCount > 1 ? 's' : ''})</span>
                            <span>${revenue.toFixed(2)}</span>
                          </div>
                        );
                      })}
                      <div className="flex justify-between border-t pt-2 font-semibold text-gray-900">
                        <span>Total Route Revenue</span>
                        <span>
                          ${formData.recipients.reduce((total, r) => {
                            return total + (r.studentCount * formData.availableWeeks.length * adjustedRate);
                          }, 0).toFixed(2)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 italic mt-2">
                        * Individual payments may be lower if customers have referral discounts
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Add recipients and select weeks to see projected revenue
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Step 6: Review & Send */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Response Deadline
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={formData.expiresInDays}
                          onChange={(e) => updateFormData({ expiresInDays: parseInt(e.target.value) })}
                          min="1"
                          max="90"
                          className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                        <span className="text-gray-600">days from now</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.autoOfferWaitlist}
                        onChange={(e) => updateFormData({ autoOfferWaitlist: e.target.checked })}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Automatically offer to waitlist when spots become available
                      </label>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.sendImmediately}
                        onChange={(e) => updateFormData({ sendImmediately: e.target.checked })}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Send offer emails immediately
                      </label>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Summary</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Route:</span>
                        <p className="font-medium">{formData.routeName || 'Not set'}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Capacity:</span>
                        <p className="font-medium">{formData.maxStudents} students</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Available Weeks:</span>
                        <p className="font-medium">{formData.availableWeeks.length} weeks</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Recipients:</span>
                        <p className="font-medium">{formData.recipients.length} recipients</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Weekly Rate:</span>
                        <p className="font-medium">${adjustedRate.toFixed(2)}/week</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Expires In:</span>
                        <p className="font-medium">{formData.expiresInDays} days</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Test Email Section */}
                <div className="border-t pt-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <TestTube2 className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-blue-900">Test Email Preview</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          Send a preview of the route offer email to your account to verify all information and formatting before sending to customers.
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleSendTestEmail}
                      disabled={sendingTestEmail || !formData.routeName.trim()}
                      className="w-full border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      <TestTube2 className="w-4 h-4 mr-2" />
                      {sendingTestEmail ? 'Sending Test Email...' : 'Send Test Email to My Account'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
          >
            Previous
          </Button>

          <div className="flex gap-3">
            <Button variant="outline" onClick={handleSaveDraft}>
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </Button>

            {currentStep < 6 ? (
              <Button onClick={() => setCurrentStep(Math.min(6, currentStep + 1))}>
                Next
              </Button>
            ) : (
              <Button onClick={handleSendOffer} disabled={loading}>
                <Send className="w-4 h-4 mr-2" />
                {formData.sendImmediately ? 'Send Offer Now' : 'Create Offer'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
